"use client";

/**
 * TransformationBlock — voor/na foto met auto-cross-fade + 4 layout-varianten.
 *
 * Port van Claude Design "video-blok/Video Blok.html" (4 variants). De BeforeAfter
 * animatie loopt elke `loop_sec` tussen voor- en na-foto. De content rond de
 * foto kan in 4 layouts:
 *   - foto-left    : foto links, kop + body + voordelen rechts
 *   - text-left    : kop + body + klantquote links, foto rechts
 *   - overlay      : full-bleed foto met donkere gradient onderaan + tekst
 *   - steps-timeline: foto links, auto-advancerende stappen-lijst rechts
 *
 * Kleuren volgen het thema via CSS vars — elk pagina-thema werkt automatisch.
 */

import { useEffect, useRef, useState } from "react";

export type TransformationVariant =
  | "foto-left"
  | "text-left"
  | "overlay"
  | "steps-timeline";

export interface TransformationContent {
  /**
   * Eén-video modus — Vimeo/YouTube URL of direct mp4/webm-bestand. Wanneer
   * gezet renderen alle varianten één enkele video i.p.v. de voor/na
   * cross-fade. Handig voor tijdlapse-renovatie filmpjes.
   */
  video_src?: string;
  /** Mobiel-specifieke video (4:5 portrait, 1080×1350 aanbevolen). Gebruikt
   *  ipv `video_src` op viewports ≤700px. Optimaal voor smartphones. */
  video_src_mobile?: string;
  /** Start-offset in seconden voor video (bv. 3 = begin bij 0:03). */
  video_start_sec?: number;
  /**
   * Voor/na cross-fade modus — wordt genegeerd als `video_src` is gezet.
   */
  before_src?: string;
  before_alt?: string;
  after_src?: string;
  after_alt?: string;
  /** Mobiel-specifieke voor/na foto's (4:5 portrait, 1080×1350 aanbevolen). */
  before_src_mobile?: string;
  after_src_mobile?: string;
  loop_sec?: number;
  /** Aspect-ratio van het foto-frame op desktop. Default 16/9 voor de meeste
   *  varianten, 21/10 voor 'overlay' variant. Override per page-block voor
   *  foto's die hoger zijn (bv staande badkamerfoto: 4/3 of 5/4). */
  image_aspect_ratio?: string;
  /** Niche-specifieke alt/title voor video. {{city}} wordt vervangen door
   *  page-city of weggestript op niche-pages. Default = "Video
   *  badkamerrenovatie [city] - transformatie van oud naar nieuw". */
  video_alt?: string;
  eyebrow?: string;
  headline?: string;
  headline_highlight?: string;
  body?: string;
  bullets?: string[];
  quote?: { text: string; author?: string };
  steps?: Array<{ time_label: string; title: string }>;
  /** Labels op de foto — alleen in cross-fade modus. */
  phase_before?: string;
  phase_after?: string;
  /** Label rechtsonder op de foto. Leeg = verbergen. */
  live_label?: string;
  /**
   * CSS object-position voor de video/foto's — bv. "left center", "30% center",
   * "right top". Default = "center". Handig wanneer een landscape-video op
   * mobiel portrait gecropt wordt en het belangrijkste onderwerp niet in het
   * midden zit.
   */
  media_object_position?: string;
}

interface Props {
  content: TransformationContent;
  variant?: TransformationVariant;
  /** Voor Zwaluw-host: rendert "25.000+ Badkamers geplaatst" badge
   *  onder de bullets in foto-left variant. */
  domain?: { domain?: string };
}

const T = {
  bg: "var(--cream-dark, #F1F3F6)",
  card: "var(--cream, #FFFFFF)",
  accent: "var(--sage, #1E3A5F)",
  accentSoft: "var(--sage-light, #D3DDE8)",
  ink: "var(--ink, #141C28)",
  muted: "var(--stone, #5A6578)",
  border: "rgba(20,28,40,0.10)",
  cta: "var(--terracotta, #FF6B1A)",
};

/**
 * Per-variant copy-defaults uit het Claude Design ontwerp. Worden enkel
 * toegepast wanneer de admin het betreffende veld leeg laat — elk stuk tekst
 * blijft dus overschrijfbaar via `content.*`.
 */
const DEFAULTS: Record<TransformationVariant, {
  eyebrow: string;
  headline: string;
  headline_highlight?: string;
  body: string;
  bullets?: string[];
  quote?: { text: string; author: string };
  steps?: Array<{ time_label: string; title: string }>;
}> = {
  "foto-left": {
    eyebrow: "Zie het in 10 seconden",
    headline: "Van verouderd naar droombadkamer.",
    headline_highlight: "",
    body:
      "Binnen gemiddeld 3 à 5 dagen transformeren wij uw oude badkamer naar een moderne, veilige en onderhoudsvrije ruimte. Compleet verzorgd — van sloop tot laatste siliconenkit.",
    bullets: [
      "Totaalrenovatie in 3 à 5 dagen",
      "Snelle levertijd: gemiddeld 4 weken",
      "Eigen vakmensen — geen onderaannemers",
    ],
  },
  "text-left": {
    eyebrow: "Het resultaat spreekt voor zich",
    headline: "Kijk mee hoe een badkamer transformeert.",
    body:
      "Geen stoffige bouwplaats — bij ons is sloop, tegelwerk, loodgieter en afwerking volledig in eigen beheer. Marktleider in Nederland met 25.000+ tevreden klanten.",
    quote: {
      text: "Dinsdag sloop, vrijdag douchen. Ongelooflijk hoe snel én netjes.",
      author: "Hendrik (68), Amstelveen · beoordeeld met 9,1",
    },
  },
  overlay: {
    eyebrow: "Van oud naar nieuw · 10 sec",
    headline: "Uw badkamer, volledig vernieuwd in 3 à 5 dagen.",
    body:
      "Compleet ontzorgd — gratis 3D-ontwerp, eigen vakmensen, levertijd van slechts 4 weken.",
  },
  "steps-timeline": {
    eyebrow: "",
    headline: "In 3 à 5 werkdagen een nieuwe badkamer.",
    body:
      "Eigen vakmensen, 4 weken levertijd — u weet exact wat er wanneer gebeurt.",
    steps: [
      { time_label: "Week 1–4", title: "Gratis 3D-ontwerp & levering" },
      { time_label: "Dag 1", title: "Sloop & ruwbouw" },
      { time_label: "Dag 2–3", title: "Tegels, leidingen & wanden" },
      { time_label: "Dag 4–5", title: "Afwerking & oplevering" },
    ],
  },
};

/**
 * Detect of viewport ≤700px is. Mirror van de mobile-breakpoint die in alle
 * variant-styles gebruikt wordt. SSR-veilig: returnt false op server,
 * client-update direct op mount via matchMedia.
 */
function useIsMobile(): boolean {
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(max-width: 700px)");
    setIsMobile(mq.matches);
    const onChange = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  return isMobile;
}

function unwrapStringList(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((item) => {
      if (typeof item === "string") return item;
      if (item && typeof item === "object" && "value" in item) {
        const v = (item as { value?: unknown }).value;
        return typeof v === "string" ? v : "";
      }
      return "";
    })
    .filter((s) => s.trim().length > 0);
}

function isVideoUrl(src: string | undefined): boolean {
  if (!src) return false;
  return /\.(mp4|webm|mov|ogg|m4v)(\?.*)?$/i.test(src);
}

/**
 * Extractor voor Vimeo / YouTube URLs. Retourneert een genormaliseerde
 * embed-URL met autoplay/mute/loop zodat de video zonder interactie afspeelt
 * (background-video stijl). `null` = niet herkenbaar als Vimeo/YouTube.
 */
function toEmbedUrl(raw: string, startSec?: number): string | null {
  try {
    const u = new URL(raw);
    // Vimeo: vimeo.com/<id>  of  player.vimeo.com/video/<id>
    const vimeoMatch =
      u.hostname.includes("vimeo.com") &&
      (u.pathname.match(/\/video\/(\d+)/) ?? u.pathname.match(/^\/(\d+)/));
    if (vimeoMatch) {
      const id = vimeoMatch[1];
      const params = new URLSearchParams({
        background: "1",
        autoplay: "1",
        loop: "1",
        muted: "1",
        byline: "0",
        title: "0",
        portrait: "0",
        dnt: "1",
      });
      const hash = startSec && startSec > 0 ? `#t=${Math.floor(startSec)}s` : "";
      return `https://player.vimeo.com/video/${id}?${params.toString()}${hash}`;
    }
    // YouTube: youtube.com/watch?v=<id>  of  youtu.be/<id>
    let ytId: string | null = null;
    if (u.hostname.includes("youtube.com") && u.searchParams.get("v")) {
      ytId = u.searchParams.get("v");
    } else if (u.hostname === "youtu.be") {
      ytId = u.pathname.slice(1);
    } else if (u.hostname.includes("youtube.com") && u.pathname.startsWith("/embed/")) {
      ytId = u.pathname.split("/embed/")[1] ?? null;
    }
    if (ytId) {
      const params = new URLSearchParams({
        autoplay: "1",
        mute: "1",
        loop: "1",
        playlist: ytId,
        controls: "0",
        modestbranding: "1",
        rel: "0",
        playsinline: "1",
      });
      if (startSec && startSec > 0) params.set("start", String(Math.floor(startSec)));
      return `https://www.youtube.com/embed/${ytId}?${params.toString()}`;
    }
    return null;
  } catch {
    return null;
  }
}

function SingleVideo({ src, startSec, city, objectPosition, videoAlt }: { src: string; startSec?: number; city?: string; objectPosition?: string; videoAlt?: string }) {
  const embed = toEmbedUrl(src, startSec);
  // Voorkeur: expliciete videoAlt via block-content (niche-rich).
  // Fallback: city-aware default. Anders: generic.
  const videoTitle = videoAlt
    ? (city ? videoAlt.replace(/\{\{\s*city\s*\}\}/gi, city) : videoAlt.replace(/\s*\{\{\s*city\s*\}\}\s*/gi, " ").trim())
    : (city
        ? `Video badkamerrenovatie ${city} - transformatie van oud naar nieuw`
        : "Video badkamerrenovatie - transformatie van oud naar nieuw");

  // Lazy-mount: pas <iframe>/<video> mounten wanneer 't blok in viewport komt.
  // Dit voorkomt dat de 4MB MP4 of 600KB Vimeo-iframe meeladen tijdens het
  // initial paint terwijl de visiteur nog bovenaan de pagina is. Vooral op
  // mobiel scheelt dit ~10-15 Lighthouse Performance punten.
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    if (inView) return;
    const el = wrapperRef.current;
    if (!el || typeof IntersectionObserver === "undefined") {
      // SSR-fallback: oudere browser zonder IO → meteen laden.
      setInView(true);
      return;
    }
    // 200px rootMargin = video begint laden net voordat 'ie in viewport komt
    // → bezoeker ziet 'm zo goed als meteen wanneer 'ie er bij scrollt.
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setInView(true);
          io.disconnect();
        }
      },
      { rootMargin: "200px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [inView]);

  return (
    <div ref={wrapperRef} style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}>
      {!inView ? (
        // Placeholder tot in-view: warme gradient die past in het thema.
        // Geen poster-image-fetch nodig — voorkomt extra image-request.
        <div
          role="img"
          aria-label={videoTitle}
          style={{
            position: "absolute",
            inset: 0,
            background: "linear-gradient(135deg, #2A3340 0%, #1A1F26 100%)",
          }}
        />
      ) : embed ? (
        <iframe
          src={embed}
          title={videoTitle}
          allow="autoplay; fullscreen; picture-in-picture"
          allowFullScreen
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            border: "none",
            display: "block",
          }}
        />
      ) : (
        <video
          src={src}
          aria-label={videoTitle}
          muted
          autoPlay
          loop
          playsInline
          preload="metadata"
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
            objectPosition: objectPosition ?? "center",
          }}
        />
      )}
      {/* LIVE DEMO badge — alleen op desktop, mobiel verborgen via media query */}
      {inView ? (
        <>
          <style>{`@keyframes tb-pulse { 0%,100% {opacity:1} 50% {opacity:0.3} } @media (max-width: 700px){ .tb-live-label { display: none !important; } }`}</style>
          <div
            className="tb-live-label"
            style={{
              position: "absolute",
              bottom: 14,
              right: 14,
              display: "flex",
              alignItems: "center",
              gap: 6,
              padding: "5px 10px",
              borderRadius: 999,
              background: "rgba(0,0,0,0.55)",
              color: "white",
              fontSize: 10,
              fontWeight: 600,
              letterSpacing: "0.05em",
              fontFamily: "system-ui",
              zIndex: 2,
            }}
          >
            <span style={{ width: 6, height: 6, borderRadius: 999, background: "#FF4444", animation: "tb-pulse 1.5s infinite" }} />
            LIVE DEMO · 10s
          </div>
        </>
      ) : null}
    </div>
  );
}

function mediaLayerStyle(opacity: number, objectPosition?: string): React.CSSProperties {
  return {
    position: "absolute",
    inset: 0,
    width: "100%",
    height: "100%",
    objectFit: "cover",
    objectPosition: objectPosition ?? "center",
    opacity,
    transition: "opacity 0.2s linear",
  };
}

function MediaLayer({
  src,
  alt,
  opacity,
  objectPosition,
}: {
  src: string;
  alt: string;
  opacity: number;
  objectPosition?: string;
}) {
  if (isVideoUrl(src)) {
    return (
      <video
        src={src}
        aria-label={alt}
        muted
        autoPlay
        loop
        playsInline
        preload="metadata"
        style={mediaLayerStyle(opacity, objectPosition)}
      />
    );
  }
  // eslint-disable-next-line @next/next/no-img-element
  return <img src={src} alt={alt} loading="lazy" style={mediaLayerStyle(opacity, objectPosition)} />;
}

// ── Animatie kern ──────────────────────────────────────────────────────────
function BeforeAfterMedia(props: Omit<TransformationContent, "eyebrow" | "headline" | "headline_highlight" | "body" | "bullets" | "quote" | "steps"> & { city?: string }) {
  const isMobile = useIsMobile();
  // Single-video modus heeft voorrang als `video_src` is gezet.
  if (props.video_src && props.video_src.trim().length > 0) {
    const videoSrc = (isMobile && props.video_src_mobile?.trim()) ? props.video_src_mobile : props.video_src;
    return (
      <div style={{ position: "relative", width: "100%", height: "100%", overflow: "hidden", background: "#1a1918" }}>
        <SingleVideo
          src={videoSrc}
          {...(props.video_start_sec !== undefined ? { startSec: props.video_start_sec } : {})}
          {...(props.city ? { city: props.city } : {})}
          {...(props.video_alt ? { videoAlt: props.video_alt } : {})}
          {...(props.media_object_position ? { objectPosition: props.media_object_position } : {})}
        />
      </div>
    );
  }
  return <BeforeAfterCrossfade {...props} />;
}

function BeforeAfterCrossfade({
  before_src,
  before_alt,
  after_src,
  after_alt,
  before_src_mobile,
  after_src_mobile,
  loop_sec = 10,
  phase_before = "VOORHEEN",
  phase_after = "NA RENOVATIE",
  live_label = "LIVE DEMO · 10s",
  media_object_position,
  city,
}: Omit<TransformationContent, "eyebrow" | "headline" | "headline_highlight" | "body" | "bullets" | "quote" | "steps" | "video_src" | "video_start_sec"> & { city?: string }) {
  const isMobile = useIsMobile();
  const beforeFinal = (isMobile && before_src_mobile?.trim()) ? before_src_mobile : before_src;
  const afterFinal = (isMobile && after_src_mobile?.trim()) ? after_src_mobile : after_src;
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    let raf: number;
    const start = performance.now();
    const tick = (now: number) => {
      const sec = ((now - start) / 1000) % loop_sec;
      const pre = Math.max(1.5, loop_sec * 0.4);
      const fade = Math.max(0.5, loop_sec * 0.1);
      const post = pre + fade + Math.max(1.5, loop_sec * 0.4);
      let p = 0;
      if (sec < pre) p = 0;
      else if (sec < pre + fade) p = (sec - pre) / fade;
      else if (sec < post) p = 1;
      else p = Math.max(0, 1 - (sec - post) / fade);
      setProgress(p);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [loop_sec]);

  return (
    <div style={{ position: "relative", width: "100%", height: "100%", overflow: "hidden", background: "#1a1918" }}>
      <style>{`@keyframes tb-pulse { 0%,100% {opacity:1} 50% {opacity:0.3} }`}</style>
      {beforeFinal ? (
        <MediaLayer
          src={beforeFinal}
          alt={before_alt ?? `Voor renovatie${city ? ` ${city}` : ""}`}
          opacity={1 - progress}
          {...(media_object_position ? { objectPosition: media_object_position } : {})}
        />
      ) : null}
      {afterFinal ? (
        <MediaLayer
          src={afterFinal}
          alt={after_alt ?? `Na renovatie${city ? ` ${city}` : ""}`}
          opacity={progress}
          {...(media_object_position ? { objectPosition: media_object_position } : {})}
        />
      ) : null}

      {/* Swipe reveal line — alleen zichtbaar tijdens fade (0.05 < p < 0.95). */}
      {progress > 0.05 && progress < 0.95 ? (
        <div
          aria-hidden
          style={{
            position: "absolute",
            top: 0,
            bottom: 0,
            left: `${progress * 100}%`,
            width: 3,
            background: T.cta,
            boxShadow: `0 0 20px ${T.cta}`,
            pointerEvents: "none",
            zIndex: 3,
          }}
        >
          <div
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              background: T.cta,
              color: "white",
              padding: "6px 10px",
              borderRadius: 999,
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: "0.05em",
              whiteSpace: "nowrap",
              fontFamily: "system-ui",
            }}
          >
            NIEUW
          </div>
        </div>
      ) : null}

      <div
        style={{
          position: "absolute",
          top: 16,
          left: 16,
          padding: "6px 12px",
          borderRadius: 999,
          background: "rgba(0,0,0,0.65)",
          color: "white",
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: "0.08em",
          fontFamily: "system-ui",
          backdropFilter: "blur(4px)",
          zIndex: 2,
        }}
      >
        {progress < 0.5 ? phase_before : phase_after}
      </div>

      {live_label ? (
        <>
          <style>{`@media (max-width: 700px){ .tb-live-label { display: none !important; } }`}</style>
          <div
            className="tb-live-label"
            style={{
              position: "absolute",
              bottom: 14,
              right: 14,
              display: "flex",
              alignItems: "center",
              gap: 6,
              padding: "5px 10px",
              borderRadius: 999,
              background: "rgba(0,0,0,0.55)",
              color: "white",
              fontSize: 10,
              fontWeight: 600,
              letterSpacing: "0.05em",
              fontFamily: "system-ui",
              zIndex: 2,
            }}
          >
            <span style={{ width: 6, height: 6, borderRadius: 999, background: "#FF4444", animation: "tb-pulse 1.5s infinite" }} />
            {live_label}
          </div>
        </>
      ) : null}
    </div>
  );
}

// ── Variant 1: foto/video links, tekst + checklist rechts ─────────────────
function FotoLeft({ content, isZwaluw = false, country = "Nederland" }: { content: TransformationContent; isZwaluw?: boolean; country?: string }) {
  const D = DEFAULTS["foto-left"];
  const bulletsRaw = unwrapStringList(content.bullets);
  const bullets = bulletsRaw.length > 0 ? bulletsRaw : D.bullets ?? [];
  const eyebrow = content.eyebrow ?? D.eyebrow;
  const body = content.body ?? D.body;
  const customHeadline = content.headline;
  return (
    <section
      style={{
        background: T.bg,
        padding: "48px 0",
        fontFamily: "'Inter', system-ui, sans-serif",
        color: T.ink,
      }}
    >
      <style>{`
        @media (max-width: 1024px){
          .tb-grid-a { grid-template-columns: 1fr !important; padding: 0 12px !important; }
          .tb-foto-frame { aspect-ratio: 4/5 !important; }
        }
      `}</style>
      <div
        className="tb-grid-a"
        style={{
          maxWidth: 1200,
          margin: "0 auto",
          padding: "0 28px",
          display: "grid",
          gridTemplateColumns: "1.15fr 1fr",
          gap: 40,
          alignItems: "center",
        }}
      >
        <div
          className="tb-foto-frame"
          style={{
            aspectRatio: content.image_aspect_ratio ?? "4/3",
            borderRadius: 14,
            overflow: "hidden",
            boxShadow: "0 30px 60px -20px rgba(0,0,0,0.25), 0 10px 20px -10px rgba(0,0,0,0.15)",
            border: `1px solid ${T.border}`,
          }}
        >
          <BeforeAfterMedia {...content} />
        </div>
        <div>
          {eyebrow ? (
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                padding: "6px 12px 6px 8px",
                borderRadius: 999,
                background: T.accentSoft,
                color: T.accent,
                fontSize: 12,
                fontWeight: 700,
                letterSpacing: "0.04em",
                marginBottom: 18,
                textTransform: "uppercase",
              }}
            >
              <span style={{ width: 6, height: 6, borderRadius: 999, background: T.cta }} />
              {eyebrow}
            </div>
          ) : null}
          <h2
            style={{
              fontFamily: "'Fraunces', serif",
              fontSize: "clamp(28px, 3.6vw, 40px)",
              fontWeight: 500,
              lineHeight: 1.08,
              letterSpacing: "-0.02em",
              color: T.ink,
              margin: "0 0 18px",
              textWrap: "balance",
            }}
          >
            {customHeadline ? (
              <>
                {customHeadline}
                {content.headline_highlight ? (
                  <> <span style={{ color: T.cta }}>{content.headline_highlight}</span></>
                ) : null}
              </>
            ) : (
              <>
                Van <span style={{ fontStyle: "italic" }}>verouderd</span>
                <br />
                naar <span style={{ color: T.cta }}>droombadkamer.</span>
              </>
            )}
          </h2>
          {body ? (
            <p style={{ fontSize: 16, lineHeight: 1.6, color: T.muted, margin: "0 0 24px" }}>{body}</p>
          ) : null}
          {bullets.length > 0 ? (
            <div style={{ display: "grid", gap: 10 }}>
              {bullets.map((b, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 14, color: T.ink }}>
                  <span
                    style={{
                      width: 18,
                      height: 18,
                      borderRadius: 999,
                      background: T.accent,
                      color: "white",
                      display: "grid",
                      placeItems: "center",
                      fontSize: 11,
                      fontWeight: 700,
                      flexShrink: 0,
                    }}
                  >
                    ✓
                  </span>
                  {b}
                </div>
              ))}
            </div>
          ) : null}
          {isZwaluw ? (
            <div
              style={{
                marginTop: 22,
                display: "inline-flex",
                alignItems: "baseline",
                gap: 14,
                padding: "14px 20px",
                background: "var(--cream, #FAF8F2)",
                border: "1px solid var(--cream-dark, #ECE7DA)",
                borderRadius: 14,
                boxShadow: "0 6px 14px -8px rgba(20,28,40,0.10)",
                flexWrap: "wrap",
              }}
            >
              <span
                style={{
                  fontFamily: "'Fraunces', Georgia, serif",
                  fontStyle: "italic",
                  fontWeight: 500,
                  fontSize: 30,
                  lineHeight: 1,
                  color: "var(--terracotta, #D96A2A)",
                  letterSpacing: "-0.01em",
                }}
              >
                25.000+
              </span>
              <span style={{ display: "flex", flexDirection: "column", lineHeight: 1.35 }}>
                <strong style={{ fontSize: 14, fontWeight: 600, color: "var(--ink, #1F2624)" }}>
                  Badkamers geplaatst
                </strong>
                <span style={{ fontSize: 13, color: "var(--stone, #6B7570)" }}>
                  in heel {country} · sinds 2013
                </span>
              </span>
            </div>
          ) : null}
        </div>
      </div>
    </section>
  );
}

// ── Variant 2: tekst + quote links, foto rechts ────────────────────────────
function TextLeft({ content }: { content: TransformationContent }) {
  const D = DEFAULTS["text-left"];
  const eyebrow = content.eyebrow ?? D.eyebrow;
  const headline = content.headline ?? D.headline;
  const body = content.body ?? D.body;
  const quote = content.quote ?? D.quote;
  return (
    <section
      style={{
        background: T.bg,
        padding: "48px 0",
        fontFamily: "'Inter', system-ui, sans-serif",
        color: T.ink,
      }}
    >
      <style>{`@media (max-width: 1024px){ .tb-grid-b { grid-template-columns: 1fr !important; } .tb-grid-b > div:last-child { order: -1; } }`}</style>
      <div
        className="tb-grid-b"
        style={{
          maxWidth: 1200,
          margin: "0 auto",
          padding: "0 28px",
          display: "grid",
          gridTemplateColumns: "1fr 1.15fr",
          gap: 40,
          alignItems: "center",
        }}
      >
        <div>
          {eyebrow ? (
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.16em", color: T.cta, marginBottom: 14 }}>
              {eyebrow.toUpperCase()}
            </div>
          ) : null}
          <h2
            style={{
              fontFamily: "'Fraunces', serif",
              fontSize: "clamp(26px, 3.4vw, 38px)",
              fontWeight: 500,
              lineHeight: 1.1,
              letterSpacing: "-0.02em",
              color: T.ink,
              margin: "0 0 20px",
              textWrap: "balance",
            }}
          >
            {headline}
          </h2>
          {body ? (
            <p style={{ fontSize: 15.5, lineHeight: 1.65, color: T.muted, margin: "0 0 28px" }}>{body}</p>
          ) : null}
          {quote?.text ? (
            <>
              <div
                style={{
                  borderLeft: `3px solid ${T.cta}`,
                  paddingLeft: 16,
                  fontFamily: "'Fraunces', serif",
                  fontSize: 18,
                  fontStyle: "italic",
                  color: T.ink,
                  lineHeight: 1.5,
                  marginBottom: 8,
                }}
              >
                &ldquo;{quote.text}&rdquo;
              </div>
              {quote.author ? (
                <div style={{ fontSize: 13, color: T.muted, paddingLeft: 16 }}>— {quote.author}</div>
              ) : null}
            </>
          ) : null}
        </div>
        <div
          style={{
            aspectRatio: content.image_aspect_ratio ?? "4/3",
            borderRadius: 14,
            overflow: "hidden",
            boxShadow: "0 30px 60px -20px rgba(0,0,0,0.25)",
            border: `1px solid ${T.border}`,
          }}
        >
          <BeforeAfterMedia {...content} />
        </div>
      </div>
    </section>
  );
}

// ── Variant 3: full-bleed met onderaan tekst-overlay ───────────────────────
function Overlay({ content }: { content: TransformationContent }) {
  const D = DEFAULTS.overlay;
  const eyebrow = content.eyebrow ?? D.eyebrow;
  const headline = content.headline ?? D.headline;
  const body = content.body ?? D.body;
  const mobileStyles = `
    @media (max-width: 700px) {
      .tb-overlay-wrap { padding: 0 8px !important; }
      .tb-overlay-frame { aspect-ratio: 4/5 !important; border-radius: 14px !important; }
      .tb-overlay-text { padding: 22px 20px 24px !important; }
      .tb-overlay-text h2 { font-size: 22px !important; line-height: 1.15 !important; }
      .tb-overlay-text p { font-size: 13.5px !important; }
    }
    /* Tekst-shadow op alle viewports voor leesbaarheid op lichte foto-secties */
    .tb-overlay-text h2,
    .tb-overlay-text p {
      text-shadow: 0 2px 10px rgba(0,0,0,0.65), 0 1px 3px rgba(0,0,0,0.45);
    }
  `;
  return (
    <section style={{ background: T.bg, padding: "32px 0", fontFamily: "'Inter', system-ui, sans-serif" }}>
      <style>{mobileStyles}</style>
      <div className="tb-overlay-wrap" style={{ maxWidth: 1200, margin: "0 auto", padding: "0 28px" }}>
        <div
          className="tb-overlay-frame"
          style={{
            position: "relative",
            aspectRatio: content.image_aspect_ratio ?? "21/10",
            borderRadius: 18,
            overflow: "hidden",
            boxShadow: "0 40px 80px -20px rgba(0,0,0,0.4)",
          }}
        >
          <BeforeAfterMedia {...content} />
          {/* Donkere gradient verwijderd op verzoek user — foto blijft
              volledig zichtbaar. Tekst krijgt text-shadow voor leesbaarheid. */}
          <div
            className="tb-overlay-text"
            style={{
              position: "absolute",
              bottom: 0,
              left: 0,
              right: 0,
              padding: "40px 50px",
              color: "white",
              zIndex: 2,
            }}
          >
            {eyebrow ? (
              <div
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "5px 11px",
                  borderRadius: 999,
                  background: T.cta,
                  color: "white",
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: "0.08em",
                  marginBottom: 16,
                }}
              >
                {eyebrow.toUpperCase()}
              </div>
            ) : null}
            <h2
              style={{
                fontFamily: "'Fraunces', serif",
                fontSize: "clamp(28px, 4vw, 44px)",
                fontWeight: 500,
                lineHeight: 1.05,
                letterSpacing: "-0.02em",
                margin: "0 0 14px",
                maxWidth: 620,
                textWrap: "balance",
                color: "#FFFFFF",
              }}
            >
              {headline}
            </h2>
            {body ? (
              <p style={{ fontSize: 15.5, lineHeight: 1.5, opacity: 0.9, maxWidth: 540, margin: 0, color: "#FFFFFF" }}>{body}</p>
            ) : null}
          </div>
        </div>
      </div>
    </section>
  );
}

// ── Variant 4: foto + auto-advancerende stappen-timeline ───────────────────
function StepsTimeline({ content }: { content: TransformationContent }) {
  const D = DEFAULTS["steps-timeline"];
  const headline = content.headline ?? D.headline;
  const body = content.body ?? D.body;
  const steps = (content.steps && content.steps.length > 0 ? content.steps : D.steps) ?? [];
  const [active, setActive] = useState(0);
  useEffect(() => {
    if (steps.length === 0) return;
    const id = setInterval(() => setActive((s) => (s + 1) % steps.length), 2500);
    return () => clearInterval(id);
  }, [steps.length]);

  return (
    <section
      style={{
        background: T.bg,
        padding: "48px 0",
        fontFamily: "'Inter', system-ui, sans-serif",
        color: T.ink,
      }}
    >
      <style>{`@media (max-width: 1024px){ .tb-grid-d { grid-template-columns: 1fr !important; } }`}</style>
      <div
        className="tb-grid-d"
        style={{
          maxWidth: 1200,
          margin: "0 auto",
          padding: "0 28px",
          display: "grid",
          gridTemplateColumns: "1.2fr 1fr",
          gap: 36,
          alignItems: "center",
        }}
      >
        <div
          style={{
            aspectRatio: content.image_aspect_ratio ?? "4/3",
            borderRadius: 14,
            overflow: "hidden",
            boxShadow: "0 30px 60px -20px rgba(0,0,0,0.25)",
            border: `1px solid ${T.border}`,
          }}
        >
          <BeforeAfterMedia {...content} />
        </div>
        <div>
          <h2
            style={{
              fontFamily: "'Fraunces', serif",
              fontSize: "clamp(22px, 2.6vw, 30px)",
              fontWeight: 500,
              lineHeight: 1.12,
              letterSpacing: "-0.015em",
              color: T.ink,
              margin: "0 0 8px",
              textWrap: "balance",
            }}
          >
            {headline}
          </h2>
          {body ? (
            <p style={{ fontSize: 14, color: T.muted, margin: "0 0 24px" }}>{body}</p>
          ) : null}
          <div style={{ display: "grid", gap: 14 }}>
            {steps.map((s, i) => {
              const isActive = active === i;
              return (
                <div
                  key={i}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 14,
                    padding: "10px 14px",
                    borderRadius: 12,
                    background: isActive ? T.card : "transparent",
                    border: `1px solid ${isActive ? T.cta : "transparent"}`,
                    transition: "all 0.3s",
                  }}
                >
                  <div
                    style={{
                      width: 34,
                      height: 34,
                      borderRadius: 999,
                      background: isActive ? T.cta : T.accentSoft,
                      color: isActive ? "white" : T.accent,
                      display: "grid",
                      placeItems: "center",
                      fontSize: 13,
                      fontWeight: 700,
                      flexShrink: 0,
                      transition: "all 0.3s",
                    }}
                  >
                    {i + 1}
                  </div>
                  <div>
                    <div
                      style={{
                        fontSize: 11,
                        fontWeight: 700,
                        letterSpacing: "0.08em",
                        color: isActive ? T.cta : T.muted,
                      }}
                    >
                      {s.time_label.toUpperCase()}
                    </div>
                    <div style={{ fontSize: 15, fontWeight: 600, color: T.ink, marginTop: 1 }}>{s.title}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}

// ── Dispatcher ─────────────────────────────────────────────────────────────
export default function TransformationBlock({ content, variant = "foto-left", domain }: Props) {
  const hasMedia = !!content.video_src || !!content.before_src || !!content.after_src;
  if (!hasMedia) return null;
  // Vd erft Zwaluw foto-left layout (zelfde merk-presentatie).
  const _hd = (domain?.domain ?? "").replace(/^www\./, "");
  const isZwaluw =
    _hd.endsWith("zwaluwcomfortsanitair.nl") ||
    _hd.endsWith("veiligdouchen.nl") ||
    _hd.endsWith("veiligdouchen.be");
  const isVdBe = _hd === "veiligdouchen.be" || _hd.endsWith(".veiligdouchen.be");
  const country = isVdBe ? "Vlaanderen" : "Nederland";
  switch (variant) {
    case "text-left":
      return <TextLeft content={content} />;
    case "overlay":
      return <Overlay content={content} />;
    case "steps-timeline":
      return <StepsTimeline content={content} />;
    case "foto-left":
    default:
      return <FotoLeft content={content} isZwaluw={isZwaluw} country={country} />;
  }
}
